import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FiEdit2, FiTrash2, FiArrowLeft, FiChevronRight, FiSettings, FiTag } from "react-icons/fi";
import { Editor } from "@hugerte/hugerte-react";
import ConferenceCategoryManager from "./ConferenceCategoryManager";
import {
  normalizeCategoryColor,
  subscribeConferenceCategories,
} from "./conferenceCategories";
import "./StudentConferenceTab.css";

import "hugerte";
import "hugerte/themes/silver";
import "hugerte/icons/default";
import "hugerte/models/dom";
import "hugerte/skins/ui/oxide/skin.css";
import "hugerte/plugins/advlist";
import "hugerte/plugins/autolink";
import "hugerte/plugins/lists";
import "hugerte/plugins/link";
import "hugerte/plugins/charmap";
import "hugerte/plugins/preview";
import "hugerte/plugins/searchreplace";
import "hugerte/plugins/visualblocks";
import "hugerte/plugins/code";
import "hugerte/plugins/fullscreen";
import "hugerte/plugins/insertdatetime";
import "hugerte/plugins/table";
import "hugerte/plugins/wordcount";

function ensureHtml(content) {
  const raw = String(content || "").trim();
  if (!raw) return "";
  const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(raw);
  if (looksLikeHtml) return raw;
  return `<p>${raw.replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`;
}

function toPlainText(html) {
  if (typeof document === "undefined") return String(html || "");
  const tmp = document.createElement("div");
  tmp.innerHTML = String(html || "");
  return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
}

function genId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

/** The category shape every draft/list entry is normalised through. */
function draftFields(entry) {
  return {
    date: entry?.date || "",
    note: entry?.note || "",
    categoryId: entry?.categoryId || "",
  };
}

/** Chip styling for a category colour (soft tint + matching border/text). */
function chipStyle(color) {
  const base = normalizeCategoryColor(color);
  return { color: base, borderColor: base, background: `${base}1a` };
}

// Autosave only fires after this much idle time (no typing, no date change).
const AUTOSAVE_DELAY_MS = 1000;
// How long the bottom-of-screen autosave notice stays up once a save settles.
const AUTOSAVE_TOAST_TTL_MS = 2400;
// Minimum time the "Saving…" notice stays on screen, so a save that finishes
// instantly still shows readable text instead of flashing for a few frames.
const SAVING_NOTICE_MIN_MS = 1500;
// Total save attempts (1 initial + retries) before autosave gives up on a draft.
const MAX_AUTOSAVE_ATTEMPTS = 3;

function todayIso() {
  const now = new Date();
  return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}

function formatDate(isoDate) {
  if (!isoDate) return "";
  const parts = String(isoDate).split("-");
  if (parts.length === 3) {
    const d = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString([], { month: "long", day: "numeric", year: "numeric" });
    }
  }
  return isoDate;
}

function latestConferenceDate(conferences) {
  const rows = Array.isArray(conferences) ? conferences : [];
  let latest = null;
  for (let i = 0; i < rows.length; i += 1) {
    const entry = rows[i] || {};
    const d = entry?.date ? new Date(entry.date) : null;
    const ms = d && !Number.isNaN(d.getTime()) ? d.getTime() : Number(entry?.createdAtMs || 0);
    if (!ms) continue;
    if (!latest || ms > latest) latest = ms;
  }
  return latest ? new Date(latest) : null;
}

function buildConferenceBaselinePatch(conferences, studentTotalHours) {
  const latest = latestConferenceDate(conferences);
  if (!latest) {
    return {
      conferenceLastCompletedAt: "",
      conferenceLastCompletedHours: 0,
    };
  }

  const hours = Number(studentTotalHours);
  return {
    conferenceLastCompletedAt: latest.toISOString(),
    conferenceLastCompletedHours: Number.isFinite(hours) && hours >= 0 ? hours : 0,
  };
}

const EDITOR_INIT = {
  height: 460,
  resize: true,
  menubar: false,
  skin: false,
  content_css: false,
  ui_mode: "split",
  toolbar_mode: "wrap",
  browser_spellcheck: true,
  contextmenu: false,
  directionality: "ltr",
  content_style:
    "body { font-family: Helvetica, Arial, sans-serif; font-size: 14px; color: #0f172a; margin: 1rem; direction: ltr; unicode-bidi: embed; }",
  plugins: [
    "advlist",
    "autolink",
    "lists",
    "link",
    "charmap",
    "preview",
    "searchreplace",
    "visualblocks",
    "code",
    "fullscreen",
    "insertdatetime",
    "table",
    "wordcount",
  ],
  toolbar:
    "undo redo | blocks fontfamily fontsize | bold italic underline | forecolor backcolor | alignleft aligncenter alignright | bullist numlist | outdent indent | table | removeformat | code fullscreen",
  branding: false,
};

export default function StudentConferenceTab({ student, setField, onSaveTab, saving, permissions }) {
  const canEdit = permissions ? !!permissions.editConference : true;

  const conferences = useMemo(
    () => (Array.isArray(student?.conferences) ? student.conferences : []),
    [student?.conferences]
  );

  // Global category definitions (shared across every student, admin-editable).
  // Subscribed live so a rename/recolour lands on open notes immediately.
  const [categories, setCategories] = useState([]);
  const [categoriesError, setCategoriesError] = useState("");
  const [managerOpen, setManagerOpen] = useState(false);

  useEffect(() => {
    const unsub = subscribeConferenceCategories(
      (rows) => {
        setCategories(rows);
        setCategoriesError("");
      },
      (err) => setCategoriesError(err?.message || "Could not load categories.")
    );
    return () => unsub();
  }, []);

  const categoryById = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((c) => {
      if (c?.id) map.set(c.id, c);
    });
    return map;
  }, [categories]);

  // Resolves a note's category id to its live name/colour (undefined if the
  // category was deleted, in which case the note simply shows no tag).
  const resolveCategory = useCallback(
    (categoryId) => (categoryId ? categoryById.get(categoryId) : undefined),
    [categoryById]
  );

  // view:
  //   null                              → list
  //   { mode: "view", ... }             → read-only detail
  //   { mode: "create" | "edit", ... }  → rich text editor
  const [view, setView] = useState(null);

  // Autosave bookkeeping (editor modes only).
  const baselineRef = useRef(""); // serialized draft last persisted (or opened with)
  const debounceRef = useRef(null);

  // Bottom-of-screen autosave notice. Deliberately a plain, non-blocking element
  // (never window.alert) so autosave can never steal focus from the editor.
  const [saveToast, setSaveToast] = useState(null); // { kind: "saving" | "saved" | "error", msg } | null
  // Draft key the editor header currently reports as persisted. This lags
  // baselineRef on purpose so the Saved/Not saved label flips in step with the
  // bottom notice instead of the instant the write lands.
  const [savedKey, setSavedKey] = useState("");
  const toastTimerRef = useRef(null); // auto-hide timer for a settled notice
  const settleTimerRef = useRef(null); // defers a settled notice to honour the minimum hold
  const savingShownAtRef = useRef(0); // when the "Saving…" notice went up
  // Failed-save bookkeeping: consecutive attempts for a draft, and the draft we
  // stopped retrying so an outage cannot produce an endless save loop.
  const retryRef = useRef({ key: "", count: 0 });
  const gaveUpRef = useRef("");

  const dismissToastTimers = useCallback(() => {
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
  }, []);

  const showToast = useCallback(
    (kind, msg) => {
      dismissToastTimers();
      // Remember when the in-progress notice went up so we know how long to hold it.
      savingShownAtRef.current = kind === "saving" ? Date.now() : 0;
      setSaveToast({ kind, msg });
      // The "saving" notice persists until the save settles.
      if (kind !== "saving") {
        toastTimerRef.current = setTimeout(() => setSaveToast(null), AUTOSAVE_TOAST_TTL_MS);
      }
    },
    [dismissToastTimers]
  );

  // Swap the "Saving…" notice for its result, but never take it down sooner than
  // SAVING_NOTICE_MIN_MS — an instant write would otherwise flash text that is
  // impossible to read. Saves slower than the threshold settle immediately.
  // `persistedKey`, when given, is committed in the same update so the header's
  // Saved/Not saved label changes at exactly the same moment as the notice.
  const settleSaveToast = useCallback(
    (kind, msg, persistedKey) => {
      dismissToastTimers();
      const commit = () => {
        if (persistedKey) setSavedKey(persistedKey);
        showToast(kind, msg);
      };
      const elapsed = savingShownAtRef.current ? Date.now() - savingShownAtRef.current : Infinity;
      const remaining = SAVING_NOTICE_MIN_MS - elapsed;
      if (remaining > 0) {
        settleTimerRef.current = setTimeout(commit, remaining);
        return;
      }
      commit();
    },
    [dismissToastTimers, showToast]
  );

  // Clear any pending notice timers on unmount.
  useEffect(() => () => dismissToastTimers(), [dismissToastTimers]);

  const displayConferences = useMemo(() => {
    return conferences
      .map((entry, idx) => ({
        ...entry,
        _origIdx: idx,
        _tempId: entry.id || `legacy-${idx}`,
      }))
      .sort((a, b) => {
        const timeA = a.date ? new Date(a.date).getTime() : 0;
        const timeB = b.date ? new Date(b.date).getTime() : 0;
        const valA = Number.isNaN(timeA) ? 0 : timeA;
        const valB = Number.isNaN(timeB) ? 0 : timeB;
        if (valB !== valA) return valB - valA;

        const createdA = Number(a.createdAtMs || 0);
        const createdB = Number(b.createdAtMs || 0);
        if (createdB !== createdA) return createdB - createdA;

        return b._origIdx - a._origIdx;
      });
  }, [conferences]);

  const setConferences = useCallback((next) => setField("conferences", next), [setField]);

  const persist = useCallback(
    async (nextConferences) => {
      // `silent: true` suppresses the blocking "Saved." alert raised by the
      // student store so autosave never interrupts typing — status is reported
      // through the bottom-of-screen notice instead.
      if (!onSaveTab) return true;
      return onSaveTab(
        {
          ...student,
          conferences: nextConferences,
          ...buildConferenceBaselinePatch(nextConferences, student?.totalSessionHours),
        },
        { silent: true }
      );
    },
    [onSaveTab, student]
  );

  const draftKey = (draft) =>
    JSON.stringify({
      date: draft?.date || "",
      note: draft?.note || "",
      categoryId: draft?.categoryId || "",
    });

  // ── Upsert the current draft into the conferences array and persist ──
  const commitDraft = useCallback(
    async (entryId, mode, origIdx, draft) => {
      const realIdx = entryId ? conferences.findIndex((c) => c && c.id === entryId) : -1;
      let next;
      let usedId = entryId;

      if (realIdx >= 0) {
        next = [...conferences];
        next[realIdx] = { ...next[realIdx], date: draft.date, note: draft.note, categoryId: draft.categoryId || "" };
      } else if (mode === "edit" && origIdx >= 0 && origIdx < conferences.length) {
        // Legacy note without a stable id — replace in place and assign one.
        usedId = conferences[origIdx]?.id || genId();
        next = [...conferences];
        next[origIdx] = {
          ...next[origIdx],
          id: usedId,
          date: draft.date,
          note: draft.note,
          categoryId: draft.categoryId || "",
        };
      } else {
        usedId = entryId || genId();
        next = [
          ...conferences,
          {
            id: usedId,
            createdAtMs: Date.now(),
            date: draft.date,
            note: draft.note,
            categoryId: draft.categoryId || "",
          },
        ];
      }

      setConferences(next);
      const ok = (await persist(next)) !== false;
      return { usedId, ok };
    },
    [conferences, setConferences, persist]
  );

  // ── Debounced autosave while editing ──
  useEffect(() => {
    if (!view || view.mode === "view" || !canEdit) return undefined;
    const key = draftKey(view.draft);
    if (key === baselineRef.current) return undefined; // nothing changed yet
    if (gaveUpRef.current === key) return undefined; // stopped retrying this draft

    debounceRef.current = setTimeout(async () => {
      // Show the notice only once the save actually starts — with a multi-second
      // idle delay it would otherwise sit on screen the whole time someone types.
      showToast("saving", "Saving…");
      let result;
      try {
        result = await commitDraft(view.entryId, view.mode, view.origIdx, view.draft);
      } catch (e) {
        result = { ok: false };
      }

      if (result?.ok) {
        retryRef.current = { key: "", count: 0 };
        baselineRef.current = key;
        // Once a create/legacy note has been written, future saves match by id.
        setView((cur) =>
          (cur ? { ...cur, entryId: result.usedId, mode: cur.mode === "create" ? "edit" : cur.mode } : cur)
        );
        settleSaveToast("saved", "All changes saved automatically", key);
        return;
      }

      // A failed save leaves the baseline untouched, which re-runs this effect
      // for an automatic retry — cap it so a persistent outage cannot loop.
      const attempt = retryRef.current.key === key ? retryRef.current.count + 1 : 1;
      retryRef.current = { key, count: attempt };
      if (attempt < MAX_AUTOSAVE_ATTEMPTS) return; // keep the "Saving…" notice up
      gaveUpRef.current = key;
      settleSaveToast("error", "Auto-save failed. Your changes are still here — edit again or press Done to retry.");
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(debounceRef.current);
  }, [view, canEdit, commitDraft, showToast, settleSaveToast]);

  // Flush any pending autosave immediately (used when leaving the editor).
  const flushPending = useCallback(async () => {
    if (!view || view.mode === "view" || !canEdit) return;
    const key = draftKey(view.draft);
    if (key === baselineRef.current) return;
    clearTimeout(debounceRef.current);
    try {
      const result = await commitDraft(view.entryId, view.mode, view.origIdx, view.draft);
      if (result?.ok) {
        baselineRef.current = key;
        retryRef.current = { key: "", count: 0 };
        settleSaveToast("saved", "All changes saved automatically", key);
      } else {
        settleSaveToast("error", "Auto-save failed. Your changes are still here — please try again.");
      }
    } catch (e) {
      settleSaveToast("error", "Auto-save failed. Your changes are still here — please try again.");
    }
  }, [view, canEdit, commitDraft, settleSaveToast]);

  // Warn before a hard browser unload if an edit is mid-flight.
  useEffect(() => {
    if (!view || view.mode === "view") return undefined;
    const handler = (e) => {
      if (draftKey(view.draft) === baselineRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [view]);

  // Reset to the list whenever the student changes.
  useEffect(() => {
    setView(null);
    setSaveToast(null);
    setSavedKey("");
    dismissToastTimers();
  }, [student?.id, dismissToastTimers]);

  const openView = (origIdx) => {
    const entry = conferences[origIdx] || {};
    setSaveToast(null);
    dismissToastTimers();
    setView({ mode: "view", entryId: entry.id || "", origIdx, draft: draftFields(entry) });
  };

  const openCreate = () => {
    const draft = { date: todayIso(), note: "", categoryId: "" };
    baselineRef.current = draftKey(draft);
    setSavedKey(""); // new note is unsaved until its first write lands
    setSaveToast(null);
    dismissToastTimers();
    setView({ mode: "create", entryId: genId(), origIdx: -1, createdAtMs: Date.now(), draft });
  };

  const startEdit = () => {
    if (!view) return;
    const key = draftKey(view.draft);
    baselineRef.current = key;
    setSavedKey(key);
    setView((cur) => ({ ...cur, mode: "edit" }));
  };

  const backToList = async () => {
    await flushPending();
    setView(null);
  };

  const setDraft = (patch) => setView((cur) => (cur ? { ...cur, draft: { ...cur.draft, ...patch } } : cur));

  const handleDelete = async (origIdx) => {
    if (!canEdit) return;
    if (!window.confirm("Delete this conference note? This cannot be undone.")) return;
    clearTimeout(debounceRef.current);
    const next = conferences.filter((_, i) => i !== origIdx);
    setConferences(next);
    setView(null);
    await persist(next);
  };

  // Non-blocking autosave notice pinned to the bottom of the screen. Uses
  // aria-live so screen readers still announce saves without grabbing focus.
  const toastNode = saveToast ? (
    <div className="conf-toast-wrap" role="status" aria-live="polite">
      <div className={`conf-toast conf-toast--${saveToast.kind}`}>
        {saveToast.kind === "saving" && <span className="conf-toast__spinner" aria-hidden="true" />}
        <span className="conf-toast__msg">{saveToast.msg}</span>
      </div>
    </div>
  ) : null;

  // Inline saved/unsaved indicator for the editor title. Compares the live draft
  // against `savedKey` (the key the notice has finished reporting as saved), so it
  // stays in sync with the bottom notice rather than the raw write completing.
  // A brand-new note counts as unsaved until its first write flips mode → "edit".
  const isDirty =
    !!view &&
    view.mode !== "view" &&
    (view.mode === "create" || draftKey(view.draft) !== savedKey);

  const managerNode = managerOpen ? (
    <ConferenceCategoryManager
      categories={categories}
      loadError={categoriesError}
      onClose={() => setManagerOpen(false)}
    />
  ) : null;

  // Rendered in every branch so notices and the manager modal survive navigation
  // between the list, a note, and the editor.
  const overlayNode = (
    <>
      {toastNode}
      {managerNode}
    </>
  );

  // ── Editor (create / edit) ──
  if (view && view.mode !== "view") {
    const selectedCategory = resolveCategory(view.draft.categoryId);
    return (
      <div className="conf-editor">
        {overlayNode}
        <div className="conf-editor__bar">
          <button type="button" className="conf-editor__back" onClick={backToList}>
            <FiArrowLeft size={15} /> Back
          </button>
          <div className="conf-editor__heading">
            {view.mode === "create" ? "New Conference Note" : "Edit Conference Note"}
            <span className={`conf-editor__status${isDirty ? " conf-editor__status--dirty" : ""}`}>
              {isDirty ? "Not saved" : "Saved"}
            </span>
          </div>
          <div className="conf-editor__bar-actions">
            {view.entryId && view.origIdx >= 0 && (
              <button
                type="button"
                className="sdw-btn sdw-btn--danger sdw-btn--sm"
                onClick={() => handleDelete(view.origIdx)}
                disabled={saving}
              >
                Delete
              </button>
            )}
            <button type="button" className="sdw-btn sdw-btn--primary sdw-btn--sm" onClick={backToList} disabled={saving}>
              Done
            </button>
          </div>
        </div>

        <div className="conf-editor__meta">
          <label className="conf-editor__field">
            <span>Date</span>
            <input type="date" value={view.draft.date} onChange={(e) => setDraft({ date: e.target.value })} />
          </label>

          <div className="conf-editor__field conf-editor__field--grow">
            <div className="conf-editor__label-row">
              <span>Category</span>
              {canEdit && (
                <button
                  type="button"
                  className="conf-editor__manage"
                  onClick={() => setManagerOpen(true)}
                >
                  <FiSettings size={12} /> Manage
                </button>
              )}
            </div>
            <div className="conf-cat-picker">
              <button
                type="button"
                className={`conf-cat-chip conf-cat-chip--none${view.draft.categoryId ? "" : " is-selected"}`}
                aria-pressed={!view.draft.categoryId}
                onClick={() => setDraft({ categoryId: "" })}
              >
                No category
              </button>
              {(categories || []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`conf-cat-chip${view.draft.categoryId === c.id ? " is-selected" : ""}`}
                  style={chipStyle(c.color)}
                  aria-pressed={view.draft.categoryId === c.id}
                  onClick={() => setDraft({ categoryId: c.id })}
                >
                  {c.name}
                </button>
              ))}
              {selectedCategory === undefined && view.draft.categoryId ? (
                <span className="conf-cat-picker__note">This note's category was deleted.</span>
              ) : null}
              {(categories || []).length === 0 ? (
                <span className="conf-cat-picker__note">No categories yet.</span>
              ) : null}
            </div>
          </div>
        </div>

        <div className="conf-editor__editor">
          <Editor
            value={ensureHtml(view.draft.note)}
            onEditorChange={(html) => setDraft({ note: html })}
            init={EDITOR_INIT}
          />
        </div>
      </div>
    );
  }

  // ── Read-only detail ──
  if (view && view.mode === "view") {
    const html = ensureHtml(view.draft.note);
    const detailCategory = resolveCategory(view.draft.categoryId);
    return (
      <div className="conf-detail">
        {overlayNode}
        <div className="conf-detail__bar">
          <button type="button" className="conf-editor__back" onClick={backToList}>
            <FiArrowLeft size={15} /> Back
          </button>
          <div className="conf-detail__heading">
            <span className="conf-detail__tag">Conference Note</span>
            {detailCategory && (
              <span className="conf-cat-chip" style={chipStyle(detailCategory.color)}>
                <FiTag size={10} aria-hidden="true" /> {detailCategory.name}
              </span>
            )}
            <span className="conf-detail__date">{formatDate(view.draft.date) || "Undated"}</span>
          </div>
          {canEdit && (
            <div className="conf-editor__bar-actions">
              <button
                type="button"
                className="sdw-btn sdw-btn--danger sdw-btn--sm"
                onClick={() => handleDelete(view.origIdx)}
              >
                Delete
              </button>
              <button type="button" className="sdw-btn sdw-btn--primary sdw-btn--sm" onClick={startEdit}>
                <FiEdit2 size={14} /> Edit
              </button>
            </div>
          )}
        </div>

        {html ? (
          <div className="conf-detail__body" dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <div className="sdw-empty">This note is empty.</div>
        )}
      </div>
    );
  }

  // ── List ──
  return (
    <div className="conf-tab">
      {overlayNode}
      <div className="conf-tab__head">
        <div className="conf-tab__head-text">
          <h4>Conference Notes</h4>
          <span className="conf-tab__count">
            {displayConferences.length === 0
              ? "No notes yet"
              : `${displayConferences.length} note${displayConferences.length !== 1 ? "s" : ""}`}
          </span>
        </div>
        {canEdit && (
          <div className="conf-tab__head-actions">
            <button
              type="button"
              className="sdw-btn sdw-btn--sm"
              onClick={() => setManagerOpen(true)}
            >
              <FiSettings size={13} /> Categories
            </button>
            <button type="button" className="sdw-btn sdw-btn--primary" onClick={openCreate}>
              + New Note
            </button>
          </div>
        )}
      </div>

      {displayConferences.length === 0 ? (
        <div className="sdw-empty">
          {canEdit
            ? "No conference notes yet. Click “New Note” to add the first one."
            : "No conference notes have been recorded yet."}
        </div>
      ) : (
        <div className="conf-list">
          {displayConferences.map((entry) => {
            const preview = toPlainText(entry.note);
            const cat = resolveCategory(entry.categoryId);
            return (
              <div
                key={entry._tempId}
                className="conf-item"
                role="button"
                tabIndex={0}
                onClick={() => openView(entry._origIdx)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openView(entry._origIdx);
                  }
                }}
              >
                <div className="conf-item__main">
                  <div className="conf-item__top">
                    <div className="conf-item__title">{formatDate(entry.date) || "Undated"}</div>
                    {cat ? (
                      <span className="conf-cat-chip" style={chipStyle(cat.color)}>
                        <FiTag size={10} aria-hidden="true" /> {cat.name}
                      </span>
                    ) : (
                      <span className="conf-item__tag">Conference Note</span>
                    )}
                  </div>
                  <div className="conf-item__preview">
                    {preview || <span className="conf-item__empty">No content yet</span>}
                  </div>
                </div>
                <div className="conf-item__aside" onClick={(e) => e.stopPropagation()}>
                  {canEdit && (
                    <div className="conf-item__actions">
                      <button
                        type="button"
                        className="conf-item__icon-btn"
                        title="Edit"
                        onClick={() => {
                          // Jump straight into edit mode from the list shortcut.
                          const key = draftKey(draftFields(entry));
                          baselineRef.current = key;
                          setSavedKey(key);
                          setView({
                            mode: "edit",
                            entryId: entry.id || "",
                            origIdx: entry._origIdx,
                            draft: draftFields(entry),
                          });
                        }}
                      >
                        <FiEdit2 size={14} />
                      </button>
                      <button
                        type="button"
                        className="conf-item__icon-btn conf-item__icon-btn--danger"
                        title="Delete"
                        onClick={() => handleDelete(entry._origIdx)}
                      >
                        <FiTrash2 size={14} />
                      </button>
                    </div>
                  )}
                  <FiChevronRight className="conf-item__chevron" size={18} aria-hidden="true" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
